import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@resideo/blueprint-react";
import { FiChevronDown } from "react-icons/fi";
import styles from "./ManageDevicesSelectionBar.module.css";

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M15 5L5 15M5 5l10 10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export interface BulkAction {
  key: string;
  label: string;
  onSelect: () => void;
  destructive?: boolean;
  disabled?: boolean;
  /** Optional tooltip explaining why the action is disabled. */
  disabledReason?: string;
}

export interface ManageDevicesSelectionBarProps {
  selectedCount: number;
  onClear: () => void;
  actions?: BulkAction[];
  /**
   * Message shown in place of the action list when no bulk action applies
   * to the current selection (e.g. a mixed-type selection). When set with
   * an empty `actions` array, the dropdown renders this string and no
   * clickable items.
   */
  emptyMessage?: string;
  /**
   * Hover tooltip surfaced next to the Bulk Actions button — used to warn
   * the user about a condition that limits the available actions (e.g.
   * one or more selected devices are offline).
   */
  hoverWarning?: string;
}

const DEFAULT_ACTIONS: BulkAction[] = [
  { key: "edit-controls", label: "Edit Controls", onSelect: () => {} },
  { key: "set-schedules", label: "Set Schedules", onSelect: () => {} },
  {
    key: "check-firmware",
    label: "Check for Firmware Updates",
    onSelect: () => {},
  },
];

export const ManageDevicesSelectionBar = ({
  selectedCount,
  onClear,
  actions = DEFAULT_ACTIONS,
  emptyMessage,
  hoverWarning,
}: ManageDevicesSelectionBarProps) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Close the dropdown when clicking outside or pressing Escape.
  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsMenuOpen(false);
    };

    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [isMenuOpen]);

  const handleActionSelect = (action: BulkAction) => {
    if (action.disabled) return;
    setIsMenuOpen(false);
    action.onSelect();
  };

  return (
    <div
      className={styles.selectionBar}
      role="toolbar"
      aria-label={t("manageDevices.ariaLabels.bulkSelectionActions")}
    >
      <div className={styles.left}>
        <button
          type="button"
          className={styles.clearButton}
          onClick={onClear}
          aria-label={t("manageDevices.ariaLabels.clearSelection")}
        >
          <CloseIcon />
        </button>
        <span className={styles.count}>
          {t("manageDevices.selected", { count: selectedCount })}
        </span>
      </div>

      <div className={styles.right} ref={menuRef}>
        <div className={styles.bulkActionsWrapper}>
          <Button
            variant="primary"
            className={styles.bulkActionsButton}
            onClick={() => setIsMenuOpen((open) => !open)}
            aria-haspopup="menu"
            aria-expanded={isMenuOpen}
            aria-describedby={
              hoverWarning ? "bulk-actions-hover-warning" : undefined
            }
          >
            <span className={styles.bulkActionsLabel}>
              {t("manageDevices.bulkActionsButton")}
              <FiChevronDown
                aria-hidden="true"
                className={`${styles.caret} ${isMenuOpen ? styles.caretOpen : ""}`}
              />
            </span>
          </Button>
          {hoverWarning && (
            <div
              id="bulk-actions-hover-warning"
              className={styles.hoverWarningTooltip}
              role="tooltip"
              data-testid="bulk-actions-hover-warning"
            >
              {hoverWarning}
            </div>
          )}
        </div>

        {isMenuOpen && (
          <div className={styles.menu} role="menu">
            {actions.length === 0 && emptyMessage ? (
              <div
                className={styles.menuMessage}
                role="menuitem"
                aria-disabled="true"
                tabIndex={-1}
                data-testid="bulk-actions-empty-message"
              >
                {emptyMessage}
              </div>
            ) : (
              actions.map((action) => {
                const button = (
                  <Button
                    key={action.key}
                    variant="secondary"
                    role="menuitem"
                    className={`${styles.menuItem} ${
                      action.destructive ? styles.menuItemDestructive : ""
                    } ${action.disabled ? styles.menuItemDisabled : ""}`}
                    disabled={action.disabled}
                    aria-disabled={action.disabled || undefined}
                    data-testid={`bulk-action-${action.key}`}
                    onClick={() => handleActionSelect(action)}
                  >
                    {action.label}
                  </Button>
                );

                // Native `title` tooltips don't fire on disabled <button>s, so
                // wrap disabled items in a span that owns the tooltip.
                if (action.disabled && action.disabledReason) {
                  return (
                    <span
                      key={action.key}
                      className={styles.menuItemWrapper}
                      title={action.disabledReason}
                    >
                      {button}
                    </span>
                  );
                }

                return button;
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageDevicesSelectionBar;
